import React, { useState, useEffect } from 'react';
import { FiX, FiSearch, FiSend, FiUsers, FiPaperclip, FiMail, FiCalendar, FiFileText, FiUser, FiCheck } from 'react-icons/fi';
import '../css/Messages.css';

const ForwardModal = ({ isOpen, onClose, message, onForward, users }) => {
  const [recipients, setRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [copyAttachments, setCopyAttachments] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setRecipients([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!users) return;
    
    const allUsers = [
      ...(users.backend_users || []),
      ...(users.client_users || [])
    ];
    
    if (!searchTerm.trim()) {
      setFilteredUsers(allUsers);
    } else {
      const filtered = allUsers.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchTerm]);

  const handleRecipientToggle = (user) => {
    const isSelected = recipients.some(r => 
      r.user_type === user.user_type && 
      (r.user_id === user.id || r.client_id === user.id)
    );
    
    if (isSelected) {
      setRecipients(recipients.filter(r => 
        !(r.user_type === user.user_type && 
          (r.user_id === user.id || r.client_id === user.id))
      ));
    } else {
      const newRecipient = {
        user_type: user.user_type,
        [user.user_type === 'backend' ? 'user_id' : 'client_id']: user.id,
        name: user.name,
        email: user.email
      };
      setRecipients([...recipients, newRecipient]);
    }
  };

  const handleForward = () => {
    if (recipients.length === 0) {
      alert('Kérjük válasszon ki legalább egy címzettet!');
      return;
    }
    
    onForward(recipients, copyAttachments);
    onClose();
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('hu-HU');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content forward-modal">
        <div className="modal-header">
          <h3>
            <FiMail style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
            Továbbítás: {message?.subject || 'Üzenet'}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          {/* Eredeti üzenet előnézet */}
          <div className="forward-preview">
            <h4>
              <FiMail style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
              Eredeti üzenet
            </h4>
            <div className="preview-content">
              <div>
                <strong>Feladó:</strong>
                <span>{
                  typeof message?.from === 'object' && message.from.name 
                    ? `${message.from.name} (${message.from.email})` 
                    : (message?.from || 'Ismeretlen')
                }</span>
              </div>
              <div>
                <strong>Tárgy:</strong>
                <span>{message?.subject || 'Nincs tárgy'}</span>
              </div>
              <div>
                <strong>Dátum:</strong>
                <span>{formatDate(message?.created_at)}</span>
              </div>
              <div>
                <strong>Tartalom:</strong>
                <span>{message?.content ? (message.content.length > 100 ? message.content.substring(0, 100) + '...' : message.content) : 'Nincs tartalom'}</span>
              </div>
            </div>
          </div>

          {/* Mellékletek másolása opció */}
          {message?.attachments && message.attachments.length > 0 && (
            <div className="attachments-option">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={copyAttachments}
                  onChange={(e) => setCopyAttachments(e.target.checked)}
                />
                <span className="checkmark"></span>
                <FiPaperclip style={{ marginRight: '4px' }} />
                Mellékletek másolása ({message.attachments.length} db)
              </label>
            </div>
          )}

          {/* Címzettek kiválasztása */}
          <div className="recipients-section">
            <h4>
              <FiUsers style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
              Címzettek kiválasztása
            </h4>
            
            {/* Kiválasztott címzettek */}
            {recipients.length > 0 && (
              <div className="selected-recipients">
                <h5>
                  <FiCheck style={{ marginRight: '8px', color: 'var(--color-success)' }} />
                  Kiválasztott címzettek ({recipients.length})
                </h5>
                <div className="recipients-tags">
                  {recipients.map((recipient, index) => (
                    <span key={index} className="recipient-tag">
                      <FiUser size={14} />
                      {recipient.name || recipient.email}
                      <button 
                        className="remove-recipient"
                        onClick={() => removeRecipient(index)}
                        title="Eltávolítás"
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Keresés */}
            <div className="search-section">
              <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Keresés név vagy email alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            {/* Felhasználók listája */}
            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <div style={{ 
                  padding: '40px 20px', 
                  textAlign: 'center', 
                  color: 'var(--color-text-gray)',
                  fontSize: '0.95rem'
                }}>
                  {searchTerm ? 'Nincs találat a keresési feltételeknek megfelelően.' : 'Nincsenek elérhető felhasználók.'}
                </div>
              ) : (
                filteredUsers.map((user) => {
                  const isSelected = recipients.some(r => 
                    r.user_type === user.user_type && 
                    (r.user_id === user.id || r.client_id === user.id)
                  );
                  
                  return (
                    <div 
                      key={`${user.user_type}-${user.id}`}
                      className={`user-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleRecipientToggle(user)}
                    >
                      <div className="user-info">
                        <div className="user-name">{user.name || 'Név nélküli felhasználó'}</div>
                        <div className="user-email">{user.email}</div>
                        <div className="user-type">
                          {user.user_type === 'backend' ? 'Backend felhasználó' : 'Ügyfél'}
                        </div>
                      </div>
                      <div className="user-checkbox">
                        {isSelected && <FiCheck className="checkmark" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div style={{ color: 'var(--color-text-gray)', fontSize: '0.9rem' }}>
            {recipients.length > 0 ? `${recipients.length} címzett kiválasztva` : 'Válasszon ki legalább egy címzettet'}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Mégse
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleForward}
              disabled={recipients.length === 0}
            >
              <FiSend size={18} />
              Továbbítás
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForwardModal;
